import { pinyin as PinYin, PINYIN_DICT, STYLE_NORMAL } from '@flowus/pinyin';

export type CompareFunc<T> = (a: T, b: T) => number;

export function deriveEqualFunc<T>(cmp: CompareFunc<T>) {
  return (a: T, b: T) => {
    return cmp(a, b) === 0;
  };
}

export function number(a: number, b: number) {
  return a - b;
}

export function string(a: string, b: string) {
  return a.localeCompare(b);
}

export function reversed<T>(cmp: CompareFunc<T>, yesOrNo = true) {
  return yesOrNo
    ? (a: T, b: T) => {
        return -cmp(a, b);
      }
    : cmp;
}

export function nullsFirst<T>(cmp: CompareFunc<T>) {
  return (a: T | null | undefined, b: T | null | undefined) => {
    if (a == null && b == null) return 0;
    if (a == null) return -1;
    if (b == null) return 1;
    return cmp(a, b);
  };
}

export function nullsLast<T>(cmp: CompareFunc<T>) {
  return (a: T | null | undefined, b: T | null | undefined) => {
    if (a == null && b == null) return 0;
    if (a == null) return 1;
    if (b == null) return -1;
    return cmp(a, b);
  };
}

export function combine<T>(...cmps: CompareFunc<T>[]) {
  return (a: T, b: T) => {
    for (const cmp of cmps) {
      const r = cmp(a, b);
      if (r !== 0) return r;
    }
    return 0;
  };
}

export function array<T>(cmp: CompareFunc<T> = poly) {
  return (a: T[], b: T[]) => {
    if (a.length < b.length) return -1;
    if (a.length > b.length) return 1;
    for (let i = 0; i < a.length; i++) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const r = cmp(a[i]!, b[i]!);
      if (r !== 0) return r;
    }
    return 0;
  };
}

export function infArray<T>(cmp: CompareFunc<T | undefined> = poly) {
  return (a: T[], b: T[]) => {
    const length = Math.max(a.length, b.length);
    for (let i = 0; i < length; i++) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const r = cmp(a[i]!, b[i]);
      if (r !== 0) return r;
    }
    return 0;
  };
}

export function iterable<T>(cmp: CompareFunc<T> = poly) {
  return (a: Iterable<T>, b: Iterable<T>) => {
    const iterA = a[Symbol.iterator]();
    const iterB = b[Symbol.iterator]();
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition, no-constant-condition
    while (true) {
      const rA = iterA.next();
      const rB = iterB.next();
      if (rA.done && rB.done) {
        return 0;
      }
      if (rA.done) {
        return -1;
      }
      if (rB.done) {
        return 1;
      }

      const r = cmp(rA.value, rB.value);
      if (r !== 0) return r;
    }
  };
}

export function poly(a: any, b: any): number {
  if (Array.isArray(a) && Array.isArray(b)) {
    return array(poly)(a, b);
  }
  if (a === b) return 0;
  return a < b ? -1 : 1;
}

export function by<P, Q>(map: (p: P) => Q, cmp: CompareFunc<Q> = poly) {
  return (a: P, b: P) => {
    return cmp(map(a), map(b));
  };
}
const CHINESE_NUM_MAP: Record<string, number> = {
  一: 1,
  二: 2,
  三: 3,
  四: 4,
  五: 5,
  六: 6,
  七: 7,
  八: 8,
  九: 9,
  十: 10,
};
const ROMAN_NUM_MAP: Record<string, number> = {
  I: 1,
  II: 2,
  III: 3,
  IV: 4,
  V: 5,
  VI: 6,
  VII: 7,
  VIII: 8,
  IX: 9,
  X: 10,
};

// #region pinyin
enum CharType {
  NUMBER,
  LETTER,
  HAN,
  OTHER,
}

const ROMAN_CHARS = new Set(['I', 'V', 'X']);
const GLOBAL_TEXT_TYPE_ORDER: Record<CharType, number> = {
  [CharType.NUMBER]: 3,
  [CharType.LETTER]: 0,
  [CharType.HAN]: 1,
  [CharType.OTHER]: 2,
};

function isRomanNumeral(text: string, index: number): boolean {
  // 向前检查：罗马数字字符后面不能紧跟普通字母（即必须是独立 token）
  let end = index;
  while (end < text.length && ROMAN_CHARS.has(text.charAt(end))) {
    end++;
  }
  const next = text.charAt(end);
  // 后面是字母说明是普通单词的一部分，不是罗马数字
  if (/^[a-zA-Z]$/.test(next)) return false;
  // 前面也不能是字母
  if (index > 0 && /^[a-zA-Z]$/.test(text.charAt(index - 1))) return false;
  // 提取出的连续罗马字符必须是合法罗马数字
  const token = text.slice(index, end);
  return ROMAN_NUM_MAP[token] !== undefined;
}

function getArabicNumberKey(token: string) {
  const normalized = token.replace(/^0+/, '') || '0';
  return [normalized.length, normalized, token.length];
}

function compareTextToken(a: [CharType, any], b: [CharType, any]) {
  const typeDiff = GLOBAL_TEXT_TYPE_ORDER[a[0]] - GLOBAL_TEXT_TYPE_ORDER[b[0]];
  if (typeDiff !== 0) return typeDiff;
  return poly(a[1], b[1]);
}

function* iterKey(text: string) {
  const len = text.length;
  for (let i = 0; i < len; i++) {
    const c = text.charAt(i);
    if (/^[0-9]$/.test(c)) {
      let end = i + 1;
      while (end < len && /^[0-9]$/.test(text.charAt(end))) {
        end++;
      }
      yield [CharType.NUMBER, getArabicNumberKey(text.slice(i, end))] as [CharType, any];
      i = end - 1;
    } else if (/^[a-zA-Z]$/.test(c)) {
      yield [CharType.LETTER, [c.toLowerCase(), c]] as [CharType, any];
    } else if (CHINESE_NUM_MAP[c] !== undefined) {
      yield [CharType.NUMBER, CHINESE_NUM_MAP[c]] as [CharType, any];
    } else if (ROMAN_NUM_MAP[c] !== undefined && isRomanNumeral(text, i)) {
      let end = i;
      while (end < len && ROMAN_CHARS.has(text.charAt(end))) {
        end++;
      }
      const token = text.slice(i, end);
      yield [CharType.NUMBER, ROMAN_NUM_MAP[token] ?? ROMAN_NUM_MAP[c]] as [CharType, any];
      i = end - 1;
    } else if (PINYIN_DICT[c.charCodeAt(0)]) {
      const [py] = PinYin.single_pinyin(c, { style: STYLE_NORMAL });
      yield [CharType.HAN, [py.charAt(0), py, c]] as [CharType, any];
    } else {
      yield [CharType.OTHER, c.charCodeAt(0)] as [CharType, any];
    }
  }
}

export const pinyin = (t1: string, t2: string) => {
  return iterable(compareTextToken)(iterKey(t1), iterKey(t2));
};
// #endregion
